import React, { useEffect, useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { togglePopUp } from "@/redux-store/cartPopUp/cartPopUpSlice";
import useResponsive from "@/hooks/useResponsive";
import { useAppDispatch, useAppSelector } from "@/redux-store/hooks";
import { v4 as uuidv4 } from "uuid";

import AdditionalInfo from "./AdditionalInfo";
import Counter from "@/components/Counter";
import CartIcon from "@/assets/icons/shopping-bag.svg?react";
import Amazon from "@/assets/icons/amazon.svg?react";
import TitleRating from "./TitleRating";
import {
  useAddEditCartMutation,
  useUpadateProductMutation,
} from "@/redux-store/api/marketingApi.ts";
import { addProductToCart, setSessionId } from "@/redux-store/cart/cartSlice";

interface IProps {
  product: IProductDetails;
  scrollToDetails: () => void;
}

const ShortDescription: React.FC<IProps> = ({ product, scrollToDetails }) => {
  const { name, shortDescription, price, id, availableQuantity, amazonLink } = product;
  const [counterValue, setCounterValue] = useState(0);
  const [addEditCartMutation] = useAddEditCartMutation();
  const [updateProduct] = useUpadateProductMutation();

  const sessionId = useAppSelector((state) => state.cart.sessionId);
  const dispatch = useAppDispatch();
  // Media
  const isSmallMobile = useMediaQuery("(max-width: 340px)");
  const isDesktop = useResponsive("up", "sm");

  useEffect(() => {
    if (!sessionId) {
      const newSessionId = uuidv4();
      dispatch(setSessionId(newSessionId));
    }
  }, [sessionId, dispatch]);

  useEffect(() => {
    setCounterValue(0);
  }, [id]);

  // Add product to the cart
  const addToCart = async () => {
    if (counterValue === 0) {
      return;
    }
    const productData = {
      IdShoppingCart: null,
      IdProduct: id,
      Quantity: counterValue,
      Price: price * counterValue,
      SessionId: sessionId,
    };

    try {
      await addEditCartMutation(productData).unwrap();
      dispatch(
        addProductToCart({
          idProduct: product.id as unknown as number,
          quantity: counterValue,
          price: product.price,
        } as ICartProduct)
      );
      setCounterValue(0);
      dispatch(togglePopUp());
    } catch (error) {
      console.error(error);
    }
  };

  const handleCounterChange = async (value: number) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (value <= availableQuantity) {
      setCounterValue(value);

      try {
        await updateProduct({
          productId: id,
          quantity: 1,
          isIncrease: value < counterValue,
        }).unwrap();
      } catch (error) {
        console.error("Error updating product:", error);
      }
    }
  };

  return (
    <Box width="100%">
      {isDesktop && <TitleRating title={name} />}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "column-reverse" }}
      >
        <Box mb={{ xs: 3, md: 6 }}>
          <Typography
            variant="h3"
            fontSize={{ xs: "1.5rem", md: "2rem" }}
            color="text.primary"
            component="p"
            mb={{ xs: 2, md: 5 }}
          >
            ${price}
          </Typography>

          {availableQuantity && availableQuantity > 0 ? (
            <Box
              display="flex"
              gap={{ xs: 2, md: 3 }}
              sx={{
                "@media (max-width: 990px)": {
                  flexDirection: "column",
                },
              }}
            >
              <Counter
                value={counterValue}
                onChange={handleCounterChange}
                max={availableQuantity}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ "& svg": { stroke: "white" }, maxWidth: 358 }}
                endIcon={isSmallMobile ? "" : <CartIcon />}
                onClick={addToCart}
                disabled={counterValue === 0}
              >
                Add to Cart
              </Button>
              <Button
                fullWidth
                variant="contained"
                sx={{ maxWidth: 358, background: 'black' }}
                endIcon={isSmallMobile ? "" : <Amazon />}
                href={amazonLink}
              >
                Buy from Amazon
              </Button>
            </Box>
          ) : (
              <Button
                  fullWidth
                  variant="contained"
                  sx={{ maxWidth: 358, background: 'black' }}
                  endIcon={isSmallMobile ? "" : <Amazon />}
                  href={amazonLink}
              >
                Buy from Amazon
              </Button>
          )}
        </Box>

        <Box mb={{ xs: 0, md: 3.5 }}>
          <Typography
            variant="body1"
            color="text.secondary"
            mb={{ xs: 0.5 }}
            dangerouslySetInnerHTML={{ __html: shortDescription }}
          ></Typography>
          <Button
            variant="text"
            onClick={scrollToDetails}
            sx={{ maxWidth: "fit-content", px: 0 }}
          >
            Read More
          </Button>
        </Box>
      </Box>

      {isDesktop && <AdditionalInfo />}
    </Box>
  );
};

export default ShortDescription;
